import React from 'react';
import { View, Text, ScrollView, StyleSheet, ViewStyle } from 'react-native';
import { ClampingTool } from '@interfaces/search';
import { scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { getBoxShadowStyle } from '@styles/shadow';
import i18n from 'i18n-js';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { WithColor } from '@styles/colors';
import { ToolsTableColumn } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationToolsScreen/helpers/buildToolsTableConfig';

interface ToolsTableProps extends WithColor {
  data: ClampingTool[];
  config: ToolsTableColumn[];
  onPress: (item: ClampingTool) => void;
  maxHeight?: ViewStyle['height'];
}

export const ToolsTable: React.FC<ToolsTableProps> = ({ data, config, onPress, C, maxHeight = 400 }) => {
  const headerSize = 50;
  const lineSize = 54;

  const renderTableHeader = () => (
    <View
      style={{
        flexDirection: 'row',
        height: scaleOP(headerSize),
        backgroundColor: C('Background / Light / Field'),
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: C('Hairline / Light / No trans'),
        paddingHorizontal: scaleOP(15),
        alignItems: 'center',
        borderTopLeftRadius: scaleOP(SCREEN_BORDER_RADIUS),
        borderTopRightRadius: scaleOP(SCREEN_BORDER_RADIUS),
      }}
    >
      {config.map((item, index) => (
        <View key={`header-${item.key}-${index}`} style={{ width: item.width, paddingRight: scaleOP(10) }}>
          <Text
            style={[
              fontOP['Button 2'],
              {
                color: item.titleColor,
                fontSize: scaleOP(13),
                fontWeight: 'normal',
              },
            ]}
          >
            {i18n.t(item.title)}
          </Text>
        </View>
      ))}
    </View>
  );

  const renderTableRow = (item: ClampingTool, index: number) => (
    <TouchableElement key={`row-${item.toolId}-${index}`} onPress={() => onPress(item)}>
      {({ boxStyle, pressHoverColor }) => (
        <View
          style={[
            boxStyle,
            {
              flexDirection: 'row',
              height: scaleOP(lineSize),
              backgroundColor: C(pressHoverColor('Background / Light / Field')),
              borderBottomWidth: index === data.length - 1 ? 0 : StyleSheet.hairlineWidth,
              borderBottomColor: C('Hairline / Light / No trans'),
              paddingHorizontal: scaleOP(15),
              alignItems: 'center',
            },
            index === data.length - 1 && {
              borderBottomLeftRadius: scaleOP(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: scaleOP(SCREEN_BORDER_RADIUS),
            },
          ]}
        >
          {config.map((c, configIndex) => (
            <View key={`cell-${c.key}-${configIndex}`} style={{ width: c.width, paddingRight: scaleOP(10) }}>
              <Text
                style={[
                  fontOP[c.valueFont || 'Body'],
                  {
                    color: c.color,
                    fontSize: scaleOP(17),
                    fontWeight: 'normal',
                  },
                ]}
                numberOfLines={1}
              >
                {c.formatValue ? c.formatValue(item) : item[c.key] || ''}
              </Text>
            </View>
          ))}
        </View>
      )}
    </TouchableElement>
  );

  return (
    <View
      style={[
        getBoxShadowStyle({ type: 'Field', C }),
        {
          backgroundColor: C('Background / Light / Field'),
          borderRadius: scaleOP(SCREEN_BORDER_RADIUS),
          maxHeight: '100%',
          overflow: 'hidden',
        },
      ]}
    >
      {renderTableHeader()}
      <ScrollView
        style={{
          maxHeight: '100%',
        }}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
        contentContainerStyle={{
          maxHeight: '100%',
        }}
      >
        {data.length > 0 ? (
          data.map(renderTableRow)
        ) : (
          <View
            style={{
              height: scaleOP(lineSize),
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: C('Background / Light / Field'),
              borderBottomLeftRadius: scaleOP(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: scaleOP(SCREEN_BORDER_RADIUS),
            }}
          >
            <Text
              style={[
                fontOP['Button 2'],
                {
                  color: C('Label/Light/Primary'),
                  fontSize: scaleOP(13),
                },
              ]}
            >
              {i18n.t('No data')}
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};
